/**
 * Admin Service
 *
 * Company settings and product type management.
 */

import apiClient, {
  APIResponse,
  unwrapResponse,
} from './apiClient';
import { ENDPOINTS } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface CompanySettings {
  id: string;
  raison_sociale: string;
  forme_juridique?: string;
  capital_social?: string;
  adresse: string;
  ville?: string;
  pays?: string;
  telephone: string;
  email: string;
  site_web?: string;
  ifu: string;
  rccm: string;
  logo_url?: string;
  signature_url?: string;
  tva_default: number;
  min_deposit_percent: number;
  currency: string;
  low_stock_threshold: number;
  plafond_dommages_caution: number;
  prefix_invoice: string;
  prefix_quote: string;
  prefix_contract: string;
  prefix_loa: string;
  prefix_delivery: string;
  prefix_movement: string;
  prefix_client: string;
  // Payment methods
  modes_paiement: { code: string; label: string; active: boolean }[];
  // Workflow settings
  auto_generate_bl_on_payment: boolean;
  require_gerant_validation: boolean;
  require_entry_condition_report: boolean;
  require_exit_condition_report: boolean;
  mentions_legales?: string;
  created_at: string;
  updated_at: string;
}

export type CompanySettingsUpdate = Partial<Omit<CompanySettings, 'id' | 'logo_url' | 'signature_url' | 'created_at' | 'updated_at'>>;

export interface ProductTypeRef {
  id: string;
  libelle: string;
  base_type: 'MACHINE' | 'PART' | 'CONSUMABLE';
  description?: string;
  is_active: boolean;
  ordre: number;
  created_at: string;
  updated_at: string;
}

export interface ProductTypeCreate {
  libelle: string;
  base_type: 'MACHINE' | 'PART' | 'CONSUMABLE';
  description?: string;
  is_active?: boolean;
  ordre?: number;
}

export type ProductTypeUpdate = Partial<ProductTypeCreate>;

// =============================================================================
// Admin Service
// =============================================================================

export interface PaymentMethodOption {
  code: string;
  label: string;
  active: boolean;
}

const SETTINGS_URL = `${ENDPOINTS.ADMIN}/settings`;

export const adminService = {
  // --- Payment Methods (public) ---

  async getPaymentMethods(): Promise<PaymentMethodOption[]> {
    const response = await apiClient.get<APIResponse<PaymentMethodOption[]>>(`${ENDPOINTS.ADMIN}/payment-methods`);
    return unwrapResponse(response);
  },

  // --- Company Settings ---

  async getSettings(): Promise<CompanySettings> {
    const response = await apiClient.get<APIResponse<CompanySettings>>(SETTINGS_URL);
    return unwrapResponse(response);
  },

  async updateSettings(data: CompanySettingsUpdate): Promise<CompanySettings> {
    const response = await apiClient.put<APIResponse<CompanySettings>>(SETTINGS_URL, data);
    return unwrapResponse(response);
  },

  async uploadLogo(file: File): Promise<CompanySettings> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<APIResponse<CompanySettings>>(
      `${SETTINGS_URL}/logo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return unwrapResponse(response);
  },

  async uploadSignature(file: File): Promise<CompanySettings> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<APIResponse<CompanySettings>>(
      `${SETTINGS_URL}/signature`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return unwrapResponse(response);
  },

  async deleteLogo(): Promise<CompanySettings> {
    const response = await apiClient.delete<APIResponse<CompanySettings>>(`${SETTINGS_URL}/logo`);
    return unwrapResponse(response);
  },

  async deleteSignature(): Promise<CompanySettings> {
    const response = await apiClient.delete<APIResponse<CompanySettings>>(`${SETTINGS_URL}/signature`);
    return unwrapResponse(response);
  },

  // --- Product Types ---

  async listProductTypes(activeOnly = false): Promise<ProductTypeRef[]> {
    const response = await apiClient.get<APIResponse<ProductTypeRef[]>>(
      '/product-types',
      { params: activeOnly ? { active_only: true } : {} }
    );
    return unwrapResponse(response);
  },

  async createProductType(data: ProductTypeCreate): Promise<ProductTypeRef> {
    const response = await apiClient.post<APIResponse<ProductTypeRef>>('/product-types', data);
    return unwrapResponse(response);
  },

  async updateProductType(id: string, data: ProductTypeUpdate): Promise<ProductTypeRef> {
    const response = await apiClient.patch<APIResponse<ProductTypeRef>>(`/product-types/${id}`, data);
    return unwrapResponse(response);
  },

  async deleteProductType(id: string): Promise<void> {
    await apiClient.delete(`/product-types/${id}`);
  },
};

export default adminService;
